import { Button } from '@components'
import {
  getInvoicingBasicsRequiredDefaultValues,
  InvoicingBasicsRequiredFields
} from '@pages/SettingsPage/sections/InvoiceSettings/General/InvoicingBasicsRequiredFields'
import { useInvoicingBasicsForm } from '@pages/SettingsPage/sections/InvoiceSettings/General/useInvoicingBasicsForm'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaSave } from 'react-icons/fa'
import styled from 'styled-components'

interface Props {
  onBack: () => void
  onClose: () => void
}

export const CreateInvoiceSettingsStep: React.FC<Props> = ({ onBack, onClose }) => {
  // React Hook Form mutates `formState.errors` in place. The compiler then keeps the previous inputs.
  'use no memo'
  const [t] = useTranslation()
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
    updateMutation
  } = useInvoicingBasicsForm({
    getDefaultValues: getInvoicingBasicsRequiredDefaultValues,
    mode: 'onChange',
    onSuccess: () => {
      toast.success(t('invoicing.form.invoicingDetailsSaved'))
    }
  })

  return (
    <>
      <BackButton type="button" onClick={onBack}>
        <FaArrowLeft />
        {t('general.back')}
      </BackButton>

      <Subtitle>{t('invoicing.form.invoicingBasicsSubtitle')}</Subtitle>

      <form
        id="create-invoice-invoicing-basics-form"
        onSubmit={handleSubmit(values => updateMutation.mutate(values))}
      >
        <InvoicingBasicsRequiredFields
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
        />
        <Note>{t('invoicing.form.invoicingBasicsNote')}</Note>
      </form>

      <Footer>
        <Button type="button" onClick={onClose}>
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          form="create-invoice-invoicing-basics-form"
          intent="success"
          icon={<FaSave />}
          disabled={!isDirty || !isValid || updateMutation.isPending}
          showSpinner={updateMutation.isPending}
        >
          {t('invoicing.form.saveAndContinue')}
        </Button>
      </Footer>
    </>
  )
}

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: none;
  background: none;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.metalGray};
  }

  svg {
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Note = styled.p`
  margin: ${({ theme }) => theme.spacing.sm}rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
